/**
 * The hf-app-theme cookie: a mirror of the user's saved theme choice, for the first paint (spec
 * docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md, "Ruling 3" and "Ruling 6").
 *
 * The choice lives on the user's account (users.preferences.appTheme). The cookie lets the first-paint
 * script paint it before any request answers. It is written for every choice the person made —
 * Classic and Custom included, since Classic now switches the builder off and the page must know that
 * before the builder's classes arrive — and never for someone who has not chosen (they carry no cookie,
 * and see the app exactly as before). Its text is `<choice>.<userId>`, so a sign-in by someone else on
 * the same browser can drop it (reconcileAppThemeOwner, called by the auth store's login()).
 *
 * `Path=/`: the app's pages share no path prefix (unlike the console's /admin), so the cookie travels
 * with every same-site page request. Nothing outside lib/theme/app-themes reads it (an isolation spec
 * holds that).
 */
import { APP_THEME_COOKIE, isAppThemeChoice, type AppThemeChoice } from './app-themes'

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365
const OWNER = /^[A-Za-z0-9-]{1,64}$/

export interface AppThemeCookie {
  theme: AppThemeChoice
  /** The user the choice belongs to; null when the cookie names nobody. */
  owner: string | null
}

/** The cookie's text as a choice, or null when it is anything but `<catalogue choice>[.<plain id>]`. */
export function parseAppThemeCookieValue(value: string | null | undefined): AppThemeCookie | null {
  if (typeof value !== 'string' || value === '') return null
  const parts = value.split('.')
  if (parts.length > 2) return null
  const [theme, owner] = parts
  if (!isAppThemeChoice(theme)) return null
  if (owner === undefined) return { theme, owner: null }
  return OWNER.test(owner) ? { theme, owner } : null
}

/** A choice as the cookie's text. Throws on a choice outside the catalogue or a strange owner — never writes one. */
export function serializeAppThemeCookieValue(theme: AppThemeChoice, owner: string): string {
  if (!isAppThemeChoice(theme)) throw new Error(`Not an app theme: ${String(theme)}`)
  if (!OWNER.test(owner)) throw new Error('The cookie owner must be a plain id')
  return `${theme}.${owner}`
}

/** The exact `document.cookie` assignment that saves a choice. */
export function appThemeCookieString(theme: AppThemeChoice, owner: string): string {
  return `${APP_THEME_COOKIE}=${serializeAppThemeCookieValue(theme, owner)}; Path=/; SameSite=Lax; Max-Age=${ONE_YEAR_SECONDS}`
}

/** The exact `document.cookie` assignment that removes it. */
export const APP_THEME_COOKIE_CLEAR = `${APP_THEME_COOKIE}=; Path=/; SameSite=Lax; Max-Age=0`

/** The cookie's raw text, or null. Reads `document.cookie` unless given a cookie header. */
export function readAppThemeCookieValue(cookieString?: string): string | null {
  const source = cookieString ?? (typeof document === 'undefined' ? '' : document.cookie)
  const prefix = `${APP_THEME_COOKIE}=`
  for (const part of source.split(';')) {
    const pair = part.trim()
    if (pair.startsWith(prefix)) return pair.slice(prefix.length)
  }
  return null
}

/** The mirrored choice, or null when there is none (never chosen) or it is not a readable one. */
export function readAppThemeCookie(cookieString?: string): AppThemeCookie | null {
  return parseAppThemeCookieValue(readAppThemeCookieValue(cookieString))
}

export function clearAppThemeCookie(): void {
  if (typeof document === 'undefined') return
  document.cookie = APP_THEME_COOKIE_CLEAR
}

/**
 * Mirror a choice: `<choice>.<owner>` for a year. No choice (null — never chosen), or no signed-in user
 * to own it, removes the cookie.
 */
export function writeAppThemeCookie(theme: AppThemeChoice | null, owner: string | null | undefined): void {
  if (typeof document === 'undefined') return
  if (theme === null || !owner || !OWNER.test(owner) || !isAppThemeChoice(theme)) {
    if (readAppThemeCookieValue() !== null) clearAppThemeCookie()
    return
  }
  document.cookie = appThemeCookieString(theme, owner)
}

/**
 * A sign-in: drop a mirrored choice that belongs to someone else (a shared browser), so the next
 * person's first page is not painted in the previous person's theme. Their own choice arrives with
 * their first app page (the scope asks the account).
 */
export function reconcileAppThemeOwner(userId: string | null | undefined): void {
  const current = readAppThemeCookie()
  if (readAppThemeCookieValue() === null) return
  if (!current || !userId || current.owner !== userId) clearAppThemeCookie()
}
